import { useCallback, useEffect, useState } from "react";
import type { ListedFile } from "../net/api.ts";
import { act, read } from "../net/rally.ts";
import { isPdfPreview, textPreviewFor } from "./attachment/file-preview.ts";
import { drawsInline } from "./attachment/filing.ts";
import type { Row } from "./person/arrivals.ts";

export type { ListedFile } from "../net/api.ts";

const refreshers = new Set<{ conversation: string; again: () => void }>();

export function refreshFilesIn(conversation: string): void {
  for (const held of refreshers) if (held.conversation === conversation) held.again();
}

export function agentFileToOffer(
  files: readonly ListedFile[],
  before: ReadonlySet<string>,
  agents: ReadonlySet<string>,
  occupied: boolean,
): ListedFile | null {
  if (occupied) return null;
  return (
    [...files]
      .reverse()
      .find(
        (file) =>
          !before.has(file.id) &&
          file.addedBy !== null &&
          agents.has(file.addedBy) &&
          (drawsInline(file) || isPdfPreview(file) || textPreviewFor(file) !== null),
      ) ?? null
  );
}

export function filesByMessage(files: readonly ListedFile[]): Map<string, ListedFile[]> {
  const grouped = new Map<string, ListedFile[]>();
  for (const file of files) {
    if (!file.message) continue;
    const held = grouped.get(file.message) ?? [];
    held.push(file);
    grouped.set(file.message, held);
  }
  return grouped;
}

export type Entry =
  | { kind: "said"; key: string; at: string; row: Row }
  | {
      kind: "file";
      key: string;
      at: string;
      file: ListedFile;
    };

export function recordWith(rows: readonly Row[], files: readonly ListedFile[]): Entry[] {
  const said: Entry[] = rows.map((row) => ({ kind: "said", key: row.key, at: row.at, row }));
  const put: Entry[] = files
    .filter((file) => file.message === null)
    .map((file) => ({ kind: "file", key: `file-${file.id}`, at: file.at, file }));
  return [...said, ...put].sort((a, b) => {
    const one = Date.parse(a.at);
    const two = Date.parse(b.at);
    if (one !== two) return one - two;
    return a.kind === b.kind ? 0 : a.kind === "said" ? -1 : 1;
  });
}

export function useFilesIn(conversation: string): { files: ListedFile[]; again: () => void } {
  const [held, setHeld] = useState<{ for: string; files: ListedFile[] }>({
    for: conversation,
    files: [],
  });

  const again = useCallback(() => {
    void read<ListedFile[]>("files.in", { conversation })
      .then((said) => setHeld({ for: conversation, files: [...said].reverse() }))

      .catch(() => setHeld({ for: conversation, files: [] }));
  }, [conversation]);

  useEffect(() => {
    setHeld({ for: conversation, files: [] });
    again();
    const held = { conversation, again };
    refreshers.add(held);
    return () => {
      refreshers.delete(held);
    };
  }, [conversation, again]);

  return { files: held.for === conversation ? held.files : [], again };
}

const opened = new Map<string, string>();
const asking = new Map<string, Promise<string | null>>();

async function bring(id: string, want: "thumb" | "preview" | undefined): Promise<string | null> {
  const under = want ? `${id}:${want}` : id;
  const already = opened.get(under);
  if (already) return already;
  const running = asking.get(under);
  if (running) return running;
  const going = (async () => {
    try {
      const said = await act<{ id: string; name: string; bytes: number; url: string }>("file.open", {
        file: id,
        ...(want ? { want } : {}),
      });
      opened.set(under, said.url);
      return said.url;
    } catch {
      return null;
    } finally {
      asking.delete(under);
    }
  })();
  asking.set(under, going);
  return going;
}

export function useOpened(id: string, want?: "thumb" | "preview"): string | undefined {
  const [url, setUrl] = useState<string | undefined>(() => opened.get(want ? `${id}:${want}` : id));
  useEffect(() => {
    let alive = true;
    void bring(id, want).then((got) => {
      if (alive) setUrl(got ?? undefined);
    });
    return () => {
      alive = false;
    };
  }, [id, want]);
  return url;
}
